import { PLAN_REQUEST_PERMISSIONS, canManagePlanRequests } from './planRequestHelpers';

export const ENROLLMENT_CONFLICT_PERMISSIONS = PLAN_REQUEST_PERMISSIONS;

/**
 * Enrollment holds share one permission set with plan requests (public/v2.php:7226),
 * so they share one gate: permission-first, with the legacy staff role ids honoured
 * only while the session has no RBAC permission map.
 *
 * `permissionSource` is either the bound `hasAnyPermission` from usePermissions() or the
 * raw permission map.
 */
export const canManageEnrollmentConflicts = (user, permissionSource) =>
  canManagePlanRequests(user, permissionSource);

export const CONFLICT_STATUS_OPTIONS = [
  { label: 'Open', value: 'open' },
  { label: 'Resolved', value: 'resolved' },
  { label: 'All', value: 'all' },
];

export const CONFLICT_SEVERITY_OPTIONS = [
  { label: 'All priorities', value: '' },
  { label: 'Needs attention', value: 'blocker' },
  { label: 'Plan / commercial', value: 'commercial' },
  { label: 'Low priority', value: 'noise' },
];

export const CONFLICT_CODE_OPTIONS = [
  { label: 'All types', value: '' },
  { label: 'Shadow deal', value: 'SHADOW_DEAL' },
  { label: 'Plan / package mismatch', value: 'COMMERCIAL_DIVERGENCE' },
  { label: 'Already enrolled', value: 'MISLABEL_COMPLETE' },
  { label: 'Matches two different people', value: 'CROSS_IDENTITY' },
  { label: 'Multiple people match', value: 'IDENTITY_AMBIGUOUS' },
];

/** Short subtitle under the type label in list rows / drawer headers. */
export const CONFLICT_CODE_SUBTITLES = {
  CROSS_IDENTITY: 'Phone and email point to different people',
  IDENTITY_AMBIGUOUS: 'More than one existing person matches this row',
  SHADOW_DEAL: 'Upload looks like an existing enrollment',
  COMMERCIAL_DIVERGENCE: 'Plan, package, or heads differ from the live record',
  MISLABEL_COMPLETE: 'Row looks already complete on a live policy',
};

export const RESOLVE_ACTIONS = [
  {
    value: 'discard_staging',
    label: 'Discard this upload row',
    description: 'Ignore the upload for this person. Their live record stays unchanged.',
    why: 'Safe when the spreadsheet row is wrong or duplicate.',
    tone: 'destructive',
  },
  {
    value: 'keep_live_dismiss',
    label: 'Keep the live record',
    description: 'Mark as reviewed with no changes. The live enrollment stays as-is.',
    why: 'Use after you confirm the live record is already correct.',
    tone: 'neutral',
  },
  {
    value: 'apply_staging_to_policy',
    label: 'Update live record from upload',
    description: 'Copy the upload details onto the matched live enrollment.',
    why: 'Use when the upload is the newer, correct information.',
    tone: 'primary',
  },
  {
    value: 'link_and_apply',
    label: 'Choose the correct person & update',
    description: 'Pick which existing person this upload belongs to, then update that record.',
    why: 'Use when you know which matched person is right.',
    requiresClientId: true,
    tone: 'primary',
  },
  {
    value: 'split_reject',
    label: 'Reject — do not merge',
    description: 'Drop this upload row. Do not change either person’s live record (common when phone and email belong to different people).',
    why: 'Best when phone and email belong to different people.',
    tone: 'destructive',
  },
];

/** Plain-English guidance shown under the action picker for identity holds. */
export const IDENTITY_RESOLVE_GUIDANCE = {
  link_and_apply: 'Use this when you know which of the matched people is correct. Their live record will be updated from the upload.',
  split_reject: 'Use this when the upload mixes two people (or you cannot safely pick one). Nothing on live records will change.',
  discard_staging: 'Use this to ignore the upload row without updating anyone.',
  keep_live_dismiss: 'Use this only if you have reviewed and confirmed no change is needed.',
  apply_staging_to_policy: 'Use this when there is a single clear live record to update.',
};

export function resolveActionTone(action) {
  const meta = RESOLVE_ACTIONS.find((a) => a.value === action);
  return meta?.tone || 'neutral';
}

export function resolveActionWhy(conflict, action) {
  if (!action) return '';
  if (isIdentityMatchConflict(conflict) && IDENTITY_RESOLVE_GUIDANCE[action]) {
    return IDENTITY_RESOLVE_GUIDANCE[action];
  }
  const meta = RESOLVE_ACTIONS.find((a) => a.value === action);
  return meta?.why || meta?.description || '';
}

/** CSS tone for list priority chips (calm, not loud tags). */
export function severityTone(severity) {
  const key = String(severity || '').toLowerCase();
  if (key === 'blocker') return 'attention';
  if (key === 'commercial') return 'commercial';
  if (key === 'noise') return 'low';
  return 'default';
}

export const FIELD_LABELS = {
  name: 'Name',
  phone: 'Phone',
  email: 'Email',
  insurance_package: 'Insurance package',
  premium_type: 'Premium type',
  additional_heads: 'Additional heads',
  state: 'State',
  city: 'City',
  plans_id: 'Plan',
  policy_no: 'Policy number',
  status: 'Status',
  social_no: 'Organization',
  category: 'Category',
  id: 'Record ID',
  updated_date: 'Last updated',
};

export function fieldLabel(field) {
  if (!field) return '—';
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  return String(field).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function severityLabel(severity) {
  const key = String(severity || '').toLowerCase();
  if (key === 'blocker') return 'Needs attention';
  if (key === 'commercial') return 'Plan / commercial';
  if (key === 'noise') return 'Low priority';
  return severity ? String(severity).replace(/_/g, ' ') : '—';
}

export const SEVERITY_COLORS = {
  blocker: 'red',
  commercial: 'orange',
  noise: 'default',
};

export function severityColor(severity) {
  const key = String(severity || '').toLowerCase();
  if (key === 'blocker') return 'red';
  if (key === 'commercial') return 'orange';
  if (key === 'noise') return 'default';
  return 'blue';
}

export function conflictCodeLabel(code) {
  const found = CONFLICT_CODE_OPTIONS.find((o) => o.value === code);
  return found?.label || String(code || 'Conflict').replace(/_/g, ' ');
}

export function conflictCodeSubtitle(code) {
  return CONFLICT_CODE_SUBTITLES[code] || '';
}

export function isCrossIdentityConflict(conflict) {
  return String(conflict?.conflict_code || '') === 'CROSS_IDENTITY';
}

export function isIdentityAmbiguousConflict(conflict) {
  return String(conflict?.conflict_code || '') === 'IDENTITY_AMBIGUOUS';
}

export function isIdentityMatchConflict(conflict) {
  return isCrossIdentityConflict(conflict) || isIdentityAmbiguousConflict(conflict);
}

/** live_snapshot may be a single client, or a multi-match envelope. */
export function liveSnapshotEnvelope(conflict) {
  const live = conflict?.live_snapshot;
  if (!live || typeof live !== 'object' || Array.isArray(live)) {
    return { kind: 'empty', phoneMatches: [], emailMatches: [], candidates: [], single: null };
  }
  const phoneMatches = Array.isArray(live.phone_matches) ? live.phone_matches : [];
  const emailMatches = Array.isArray(live.email_matches) ? live.email_matches : [];
  const candidates = Array.isArray(live.candidates) ? live.candidates : [];
  if (phoneMatches.length || emailMatches.length) {
    return { kind: 'cross', phoneMatches, emailMatches, candidates, single: null };
  }
  if (candidates.length) {
    return { kind: 'ambiguous', phoneMatches, emailMatches, candidates, single: null };
  }
  if (live.name != null || live.phone != null || live.email != null || live.policy_no != null || live.id) {
    return { kind: 'single', phoneMatches: [], emailMatches: [], candidates: [], single: live };
  }
  return { kind: 'empty', phoneMatches: [], emailMatches: [], candidates: [], single: null };
}

export function personDisplayName(person) {
  if (!person || typeof person !== 'object') return 'Unknown person';
  return person.name || person.full_name || (person.id ? `Record #${person.id}` : 'Unknown person');
}

export function personShortLine(person) {
  if (!person || typeof person !== 'object') return '';
  const bits = [];
  if (person.phone) bits.push(person.phone);
  if (person.email) bits.push(person.email);
  if (person.policy_no) bits.push(`Policy ${person.policy_no}`);
  else if (person.insurance_package) bits.push(person.insurance_package);
  return bits.join(' · ');
}

/** Detail fields under the name hero on identity person cards (name shown separately). */
export const PERSON_CARD_FIELDS = [
  'phone',
  'email',
  'policy_no',
  'insurance_package',
  'premium_type',
  'additional_heads',
];

export function personCardFields(person = {}, { highlight } = {}) {
  return PERSON_CARD_FIELDS.map((field) => ({
    field,
    label: fieldLabel(field),
    value: person?.[field],
    highlight: Boolean(highlight && field === highlight),
  })).filter((row) => row.value != null && row.value !== '');
}

/** Contact value used as the path cue on cross-identity cards. */
export function personMatchContact(conflict, path) {
  const staging = conflict?.staging_snapshot || {};
  if (path === 'phone') {
    return staging.phone || conflict?.match_phone_norm || conflict?.phone || '';
  }
  if (path === 'email') {
    return staging.email || conflict?.match_email_norm || '';
  }
  return '';
}

export function stagingDisplayName(conflict) {
  const snap = conflict?.staging_snapshot || conflict?.staging || {};
  return snap.name || snap.full_name || conflict?.name || 'Unknown';
}

/**
 * List-row title + subtitle for non-technical admins.
 * Cross-identity / ambiguous get a clear “what’s wrong” line instead of match_key.
 */
export function conflictListPresentation(conflict) {
  const name = stagingDisplayName(conflict);
  const code = conflict?.conflict_code;
  const typeLabel = conflictCodeLabel(code);
  const staging = conflict?.staging_snapshot || conflict?.staging || {};
  const env = liveSnapshotEnvelope(conflict);

  if (isCrossIdentityConflict(conflict)) {
    const phonePerson = env.phoneMatches[0];
    const emailPerson = env.emailMatches[0];
    const phoneName = personDisplayName(phonePerson);
    const emailName = personDisplayName(emailPerson);
    const phoneContact = staging.phone || conflict?.match_phone_norm || conflict?.phone || phonePerson?.phone;
    const emailContact = staging.email || conflict?.match_email_norm || emailPerson?.email;
    return {
      title: name,
      typeLabel,
      subtitle: `Phone matches ${phoneName}; email matches ${emailName}`,
      detailLine: [phoneContact, emailContact].filter(Boolean).join(' · ') || conflictCodeSubtitle(code),
    };
  }

  if (isIdentityAmbiguousConflict(conflict)) {
    const count = env.candidates.length || 0;
    const names = env.candidates.slice(0, 2).map(personDisplayName).filter(Boolean);
    const more = count > 2 ? ` +${count - 2} more` : '';
    return {
      title: name,
      typeLabel,
      subtitle: count
        ? `${count} existing people match${names.length ? `: ${names.join(', ')}${more}` : ''}`
        : conflictCodeSubtitle(code),
      detailLine: staging.phone || staging.email || conflict?.phone || conflictCodeSubtitle(code),
    };
  }

  return {
    title: name,
    typeLabel,
    subtitle: conflictCodeSubtitle(code) || '',
    detailLine: staging.phone || conflict?.phone || conflict?.match_phone_norm || staging.email || '—',
  };
}

/** Drawer explanation for identity holds. */
export function conflictExplanation(conflict) {
  if (isCrossIdentityConflict(conflict)) {
    const env = liveSnapshotEnvelope(conflict);
    const phoneName = personDisplayName(env.phoneMatches[0]);
    const emailName = personDisplayName(env.emailMatches[0]);
    return {
      title: 'This upload matches two different people',
      body: `The phone number points to ${phoneName}, but the email points to ${emailName}. Pick the correct person to update, or reject the row if these should stay separate.`,
    };
  }
  if (isIdentityAmbiguousConflict(conflict)) {
    const env = liveSnapshotEnvelope(conflict);
    const count = env.candidates.length;
    return {
      title: 'Several existing people match this upload',
      body: count
        ? `We found ${count} live records that share this phone or email. Choose which person this upload belongs to, or reject the row if you are unsure.`
        : 'More than one live record matches this phone or email. Choose the correct person, or reject the row.',
    };
  }
  const subtitle = conflictCodeSubtitle(conflict?.conflict_code);
  if (subtitle) {
    return {
      title: conflictCodeLabel(conflict?.conflict_code),
      body: subtitle,
    };
  }
  return null;
}

export function resolveActionGuidance(conflict, action) {
  return resolveActionWhy(conflict, action);
}

/** People an admin can pick for link_and_apply. */
export function linkablePeople(conflict) {
  const env = liveSnapshotEnvelope(conflict);
  const byId = new Map();
  const add = (person, matchedBy) => {
    if (!person || typeof person !== 'object') return;
    const id = Number(person.id);
    if (!id) return;
    const existing = byId.get(id);
    if (existing) {
      if (matchedBy && !existing.matchedBy.includes(matchedBy)) {
        existing.matchedBy.push(matchedBy);
      }
      return;
    }
    byId.set(id, {
      id,
      person,
      matchedBy: matchedBy ? [matchedBy] : [],
      label: personDisplayName(person),
      description: [
        matchedBy ? `Matched by ${matchedBy}` : null,
        personShortLine(person),
      ].filter(Boolean).join(' · '),
    });
  };

  if (env.kind === 'cross') {
    env.phoneMatches.forEach((p) => add(p, 'phone'));
    env.emailMatches.forEach((p) => add(p, 'email'));
  } else if (env.kind === 'ambiguous') {
    env.candidates.forEach((p) => add(p, null));
  } else if (env.kind === 'single' && env.single) {
    add(env.single, null);
  }
  return [...byId.values()];
}

const HYPOTHESIS_PLAIN = [
  [/CROSS_IDENTITY[:\s].*/i, 'Phone and email match different people on file.'],
  [/IDENTITY_AMBIGUOUS[:\s].*/i, 'More than one existing person matches this phone or email.'],
  [/SHADOW_DEAL[:\s].*/i, 'This upload looks like someone already enrolled.'],
  [/AUTO_UPDATE[:\s].*/i, 'Only plan, premium, or heads changed — can update automatically.'],
  [/TRUE_PENDING[:\s].*/i, 'No existing person matched this phone or email.'],
];

export function humanizeHypothesis(raw) {
  const text = typeof raw === 'string' ? raw : (raw?.label || raw?.message || '');
  if (!text) return '';
  for (const [re, plain] of HYPOTHESIS_PLAIN) {
    if (re.test(text)) return plain;
  }
  // Strip leading CODE: prefixes for leftover technical notes
  return text.replace(/^[A-Z][A-Z0-9_]+:\s*/, '');
}

export function topDiffs(conflict, limit = 2) {
  const diffs = Array.isArray(conflict?.diff_json)
    ? conflict.diff_json
    : Array.isArray(conflict?.diffs)
      ? conflict.diffs
      : [];
  return diffs.slice(0, limit);
}

export function hypothesisHints(conflict) {
  const raw = conflict?.hypotheses_json ?? conflict?.hypotheses ?? [];
  const list = Array.isArray(raw)
    ? raw.map((h) => humanizeHypothesis(h)).filter(Boolean)
    : typeof raw === 'string'
      ? [humanizeHypothesis(raw)].filter(Boolean)
      : [];
  // Identity conflicts already have a dedicated explanation — skip redundant tech notes
  if (isIdentityMatchConflict(conflict)) {
    return list.filter((h) => !/different people|more than one existing person/i.test(h));
  }
  return list;
}

export function recommendedActions(conflict) {
  const raw = conflict?.recommended_actions_json ?? conflict?.recommended_actions ?? [];
  if (!Array.isArray(raw)) return [];
  return raw.map((a) => (typeof a === 'string' ? a : a?.action || a?.value)).filter(Boolean);
}

export function isActionRecommended(conflict, action) {
  const list = recommendedActions(conflict);
  if (!list.length) return true; // no guidance → allow all
  return list.includes(action);
}

export function isHeadsReduction(conflict) {
  if (isIdentityMatchConflict(conflict)) return false;
  const staging = conflict?.staging_snapshot || {};
  const live = conflict?.live_snapshot || {};
  if (Array.isArray(live.phone_matches) || Array.isArray(live.email_matches) || Array.isArray(live.candidates)) {
    return false;
  }
  const s = Number(staging.additional_heads);
  const l = Number(live.additional_heads);
  if (!Number.isFinite(s) || !Number.isFinite(l)) return false;
  return s < l;
}

/** True when upload/approve response still has conflicts to resolve (may already be partially applied). */
export function isEnrollmentNeedsResolution(payload) {
  const data = payload?.data ?? payload ?? {};
  const status = String(data.status || '').toLowerCase();
  return Boolean(
    data.needs_resolution
      || status === 'needs_resolution'
      || status === 'partially_applied'
      || (data.session_id && Number(data.open_conflict_count ?? data.needs_resolution_count) > 0),
  );
}

export const ENROLLMENT_APPLY_ACTION_LABELS = {
  onboarded: 'Onboarded',
  auto_updated: 'Updated live record',
  auto_dismissed: 'Same as live (dismissed)',
  needs_resolution: 'Needs conflict review',
  held_apply_error: 'Held — apply error',
};

export function enrollmentApplyResultAction(item) {
  if (!item) return '';
  const raw = item.apply_result_json;
  if (raw == null || raw === '') return '';
  let parsed = raw;
  if (typeof raw === 'string') {
    try {
      parsed = JSON.parse(raw);
    } catch (_) {
      return '';
    }
  }
  if (!parsed || typeof parsed !== 'object') return '';
  const action = String(parsed.action || '').trim();
  if (!action) return '';
  return ENROLLMENT_APPLY_ACTION_LABELS[action] || action.replace(/_/g, ' ');
}

/** Counts from beginSession / 202 pause payload or staff-upload validation_summary. */
export function enrollmentPartialApplySummary(data = {}) {
  const summary = data.summary && typeof data.summary === 'object' ? data.summary : {};
  const num = (...vals) => {
    for (const v of vals) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return 0;
  };
  const onboarded = num(
    data.onboarded_count,
    data.enrollment_onboarded_count,
    data.added_count,
    data.enrollment_added_count,
    summary.onboarded,
    summary.inserted,
  );
  const updated = num(
    data.updated_count,
    data.enrollment_updated_count,
    summary.updated,
  );
  const dismissed = num(
    data.dismissed_as_same_count,
    data.enrollment_dismissed_same_count,
    summary.dismissed_as_same,
  );
  const needs = num(
    data.needs_resolution_count,
    data.open_conflict_count,
    data.conflict_count,
    data.enrollment_conflict_count,
    summary.conflicts,
  );
  const heldVals = [data.held_clean_count, data.enrollment_held_clean_count];
  let held = 0;
  for (const v of heldVals) {
    if (v === undefined || v === null || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n)) {
      held = Math.max(0, n);
      break;
    }
  }
  return {
    onboarded,
    added: onboarded,
    updated,
    dismissed,
    needs,
    held,
    rowCount: Number(data.row_count ?? summary.total_rows) || 0,
  };
}

export function enrollmentPausedMessage(data = {}) {
  const { onboarded, updated, dismissed, needs, held } = enrollmentPartialApplySummary(data);
  const parts = [];
  if (onboarded > 0) parts.push(`${onboarded} added to the onboarding list`);
  if (updated > 0) parts.push(`${updated} updated`);
  if (dismissed > 0) parts.push(`${dismissed} dismissed as same`);
  if (needs > 0) parts.push(`${needs} conflict${needs === 1 ? '' : 's'} need review`);
  if (held > 0) parts.push(`${held} clean still held`);
  if (parts.length) {
    return parts.join(', ') + '. Already-onboarded rows are not paused.';
  }
  return `${needs} conflict${needs === 1 ? '' : 's'} need review.`;
}

export function enrollmentPausedNotification(data = {}) {
  const { added, updated, dismissed, needs } = enrollmentPartialApplySummary(data);
  const queued = Number(data.notifications_queued ?? data.summary?.notifications_queued) || 0;
  const partiallyApplied = added > 0 || updated > 0 || dismissed > 0
    || String(data.status || '').toLowerCase() === 'partially_applied';
  const description = enrollmentPausedMessage(data);
  return {
    message: partiallyApplied ? 'New policies added to onboarding' : 'Enrollment needs resolution',
    description: (description
      || (needs > 0
        ? `${needs} conflict${needs === 1 ? '' : 's'} still need resolution.`
        : 'Resolve remaining conflicts to finish.'))
      + (queued > 0 ? ` ${queued} onboarding instruction${queued === 1 ? '' : 's'} queued.` : ''),
  };
}

/**
 * Corp detail routes use base64(social_no) in the path; conflict tables and
 * enrollment-holds filters use the plain org code (e.g. KADEL, not S0FERUw=).
 * Prefer the first non-empty candidate; decode padded base64 route hashes.
 */
export function resolvePlainOrgCode(...candidates) {
  for (const raw of candidates) {
    if (raw == null || raw === '') continue;
    const value = String(raw).trim();
    if (!value) continue;
    // Route hashes are base64 of org_social_no, and base64 only carries '='
    // padding when the input length is not a multiple of three — so requiring
    // padding let unpadded hashes through as if they were the code itself, and
    // the API then could not resolve the organisation. Attempt a decode on
    // anything base64-shaped and keep it only when it yields printable text
    // that differs from the input.
    if (/^[A-Za-z0-9+/]+={0,2}$/.test(value) && value.length % 4 === 0 && typeof atob === 'function') {
      try {
        const decoded = atob(value);
        if (decoded && decoded !== value && /^[\x20-\x7E]+$/.test(decoded)) return decoded;
      } catch (_) {
        /* keep value */
      }
    }
    return value;
  }
  return '';
}

/** Cross-org enrollment holds inbox (finance work queue). */
export function enrollmentConflictsInboxPath({ sessionId, socialNo, organizationId } = {}) {
  const params = new URLSearchParams();
  const plainSocialNo = resolvePlainOrgCode(socialNo);
  if (sessionId) params.set('session_id', String(sessionId));
  if (plainSocialNo) params.set('social_no', plainSocialNo);
  if (organizationId) params.set('organization_id', String(organizationId));
  const qs = params.toString();
  return qs ? `/corporations/enrollment-holds?${qs}` : '/corporations/enrollment-holds';
}

export function isEnrollmentSchemaMissing(parsed) {
  const status = Number(parsed?.httpStatus);
  const msg = String(parsed?.message || '').toLowerCase();
  return status === 503 || msg.includes('enrollment conflict schema not migrated');
}

/** Bullet copy for bulk-clear confirmation (live policies stay untouched). */
export function clearOpenConfirmBullets() {
  return [
    'Every open hold for this org will be dismissed (staging discarded).',
    'Conflict-linked staging rows are soft-archived — not hard-deleted.',
    'Stuck upload sessions close once holds are gone; clean held rows may still apply.',
    'Live client policies are never changed or deleted.',
  ];
}

/** Human-readable summary after POST …/enrollment-conflicts/clear. */
export function formatClearOpenFeedback(data = {}, socialNo = '') {
  const org = socialNo || data.social_no || 'this org';
  const openFound = Number(data.open_found) || 0;
  const resolved = Number(data.resolved) || 0;
  const failed = Array.isArray(data.failed) ? data.failed : [];
  const sessionsClosed = Array.isArray(data.sessions_closed) ? data.sessions_closed : [];
  const heldDropped = Number(data.held_rows_dropped) || 0;
  const remainingOpen = data.summary?.open_conflicts_total;

  if (openFound === 0) {
    return {
      level: 'info',
      message: `No open holds for ${org}`,
      description: 'Nothing was changed. Live policies are unchanged.',
      failed,
    };
  }

  const message = failed.length
    ? `Cleared ${resolved} of ${openFound} hold${openFound === 1 ? '' : 's'} for ${org}`
    : `Cleared ${resolved} open hold${resolved === 1 ? '' : 's'} for ${org}`;

  const parts = [];
  if (sessionsClosed.length) {
    const applied = sessionsClosed.filter((s) => s.mode === 'applied').length;
    const cancelled = sessionsClosed.filter((s) => s.mode === 'cancelled').length;
    const sessionErrors = sessionsClosed.filter((s) => s.mode === 'error').length;
    const sessionBits = [];
    if (applied) sessionBits.push(`${applied} upload session${applied === 1 ? '' : 's'} completed`);
    if (cancelled) sessionBits.push(`${cancelled} session${cancelled === 1 ? '' : 's'} abandoned`);
    if (sessionErrors) sessionBits.push(`${sessionErrors} session close error${sessionErrors === 1 ? '' : 's'}`);
    if (sessionBits.length) parts.push(sessionBits.join(', '));
  }
  if (heldDropped > 0) {
    parts.push(`${heldDropped} held row${heldDropped === 1 ? '' : 's'} dropped`);
  }
  if (Number.isFinite(Number(remainingOpen))) {
    const left = Number(remainingOpen);
    if (left === 0) parts.push('No open holds remain for this org');
    else parts.push(`${left} open hold${left === 1 ? '' : 's'} still remain`);
  } else {
    parts.push('Live policies unchanged');
  }

  return {
    level: failed.length ? 'warning' : 'success',
    message,
    description: parts.join(' · '),
    failed,
  };
}

/** Toast copy after resolving a single conflict row. */
export function formatResolveFeedback(data = {}, conflict = null, action = '') {
  const actionMeta = RESOLVE_ACTIONS.find((a) => a.value === action);
  const name = stagingDisplayName(conflict);
  const actionLabel = actionMeta?.label || String(action || 'Resolved').replace(/_/g, ' ');
  const sessionApply = data?.session_apply;
  const parts = [`${name} — ${actionLabel}`];

  if (sessionApply?.status === 'completed') {
    const applied = Number(sessionApply.applied_this_pass ?? sessionApply.applied_count) || 0;
    parts.push(`session #${sessionApply.session_id} completed (${applied} row${applied === 1 ? '' : 's'} applied)`);
    return {
      level: 'success',
      message: 'Upload completed',
      description: parts.join(' · '),
    };
  }
  if (sessionApply?.status === 'failed') {
    const errCount = Array.isArray(sessionApply.errors) ? sessionApply.errors.length : 0;
    parts.push(`session #${sessionApply.session_id} apply failed${errCount ? ` (${errCount} row error${errCount === 1 ? '' : 's'})` : ''}`);
    return {
      level: 'warning',
      message: 'Conflict resolved — session apply had errors',
      description: parts.join(' · '),
    };
  }
  if (sessionApply?.session_id) {
    parts.push(`session #${sessionApply.session_id} still has open conflicts`);
  }

  return {
    level: 'success',
    message: 'Conflict resolved',
    description: parts.join(' · '),
  };
}

/** Empty-table copy for the holds inbox. */
export function enrollmentConflictsEmptyText({
  schemaMissing = false,
  status = 'open',
  socialNo = '',
  sessionId = '',
} = {}) {
  if (schemaMissing) return 'Enrollment conflict schema not migrated on this environment';
  if (status === 'open' && socialNo) {
    return sessionId
      ? `No open holds for ${socialNo} in session #${sessionId}`
      : `No open holds for ${socialNo}`;
  }
  if (status === 'open') return 'No open enrollment holds — backlog is clear';
  return 'No conflicts match these filters';
}

export function snapshotFieldPairs(staging = {}, live = {}) {
  const keys = [
    'name',
    'phone',
    'email',
    'insurance_package',
    'premium_type',
    'additional_heads',
    'state',
    'city',
    'plans_id',
    'policy_no',
  ];
  // Multi-match envelopes are not a single live person — compare staging only against empty live
  const liveIsEnvelope = live
    && typeof live === 'object'
    && (Array.isArray(live.phone_matches)
      || Array.isArray(live.email_matches)
      || Array.isArray(live.candidates));
  const livePerson = liveIsEnvelope ? {} : (live || {});
  const seen = new Set(keys);
  Object.keys(staging || {}).forEach((k) => {
    if (!['phone_matches', 'email_matches', 'candidates'].includes(k)) seen.add(k);
  });
  Object.keys(livePerson || {}).forEach((k) => {
    if (!['phone_matches', 'email_matches', 'candidates'].includes(k)) seen.add(k);
  });
  const ordered = [...keys, ...[...seen].filter((k) => !keys.includes(k))].slice(0, 16);
  return ordered.map((field) => ({
    field,
    label: fieldLabel(field),
    staging: staging?.[field],
    live: livePerson?.[field],
    differed: String(staging?.[field] ?? '') !== String(livePerson?.[field] ?? ''),
  }));
}
